import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import AuthImage from "../components/AuthImage";
import Spinner from "../components/Spinner";
import AddFootballItemModal from "../components/AddFootballItemModal";
import {TrashIcon} from "../components/icons";
import {FootballKit, FootballRefOptions, FOOTBALL_IMAGE_BASE} from "../service/footballTypes";
import {useDocumentTitle} from '../hooks/useDocumentTitle';

const EMPTY_REFS: FootballRefOptions = {kitTypes: [], kitVersions: [], sleevesTypes: [], sizes: []};

const FootballItemDetail: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [kit, setKit] = useState<FootballKit | null>(null);
    useDocumentTitle(kit?.club?.name);
    const [refs, setRefs] = useState<FootballRefOptions>(EMPTY_REFS);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);

    const fetchKit = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const api = await createAxiosInstance();
            const [res, r] = await Promise.all([
                api.get<FootballKit>(`/football/kits/${id}`),
                api.get<FootballRefOptions>('/football/refs'),
            ]);
            setKit(res.data);
            setRefs(r.data ?? EMPTY_REFS);
        } catch (err) {
            console.error('Error fetching football kit:', err);
            setKit(null);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchKit();
    }, [fetchKit]);

    const handleDelete = async () => {
        if (!window.confirm('Удалить комплект из коллекции?')) return;
        try {
            const api = await createAxiosInstance();
            await api.delete(`/football/kits/${id}`);
            navigate('/football');
        } catch (err) {
            console.error('Error deleting football kit:', err);
        }
    };

    if (loading) return <div className="flex justify-center py-12"><Spinner/></div>;
    if (!kit) return <div className="text-center py-12 text-gray-500 dark:text-gray-400">Комплект не найден.</div>;

    const classification: [string, string | null | undefined][] = [
        ['Клуб', kit.club?.name],
        ['Сезон', kit.season?.name],
        ['Тип', kit.kitType?.nameRu],
        ['Бренд', kit.brand?.name],
        ['Заметки', kit.notes],
    ];

    const jerseyRows: [string, string | null | undefined][] = [
        ['Версия', kit.jersey?.version?.nameRu],
        ['Размер', kit.jersey?.size?.nameRu],
        ['Номер', kit.jersey?.number != null ? `#${kit.jersey.number}` : null],
        ['Игрок', kit.jersey?.playerName],
        ['Рукава', kit.jersey?.sleevesType?.nameRu],
        ['Патч', kit.jersey?.patch?.name],
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between gap-4">
                <button onClick={() => navigate('/football')} className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">← К коллекции</button>
                <div className="flex gap-2">
                    <button onClick={() => setEditing(true)} className="btn-secondary">Редактировать</button>
                    <button onClick={handleDelete} className="p-2 rounded-xl text-gray-400 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-white/5" title="Удалить">
                        <TrashIcon className="w-5 h-5"/>
                    </button>
                </div>
            </div>

            <div className="card p-6">
                <div className="flex items-baseline gap-3 mb-1">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{kit.club?.name || 'Комплект'}</h1>
                    {kit.kitType && <span className="badge badge-info">{kit.kitType.nameRu}</span>}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{kit.season?.name}</p>

                <dl className="mt-4 divide-y divide-gray-100 dark:divide-white/5">
                    {classification.map(([label, value]) => (
                        <div key={label} className="flex justify-between py-2 text-sm">
                            <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
                            <dd className="text-gray-900 dark:text-gray-100 font-medium text-right">{value || '—'}</dd>
                        </div>
                    ))}
                </dl>
            </div>

            {/* jersey section */}
            <PartSection title="Футболка" photos={kit.jersey?.photoUrls} rows={jerseyRows}/>

            {/* shorts section (only if present) */}
            {kit.shorts && (
                <PartSection title="Шорты" photos={kit.shorts.photoUrls}
                    rows={[
                        ['Версия', kit.shorts.version?.nameRu],
                        ['Размер', kit.shorts.size?.nameRu],
                        ['Номер', kit.shorts.number != null ? `#${kit.shorts.number}` : null],
                    ]}/>
            )}

            {editing && (
                <AddFootballItemModal
                    editItem={kit}
                    refs={refs}
                    onClose={() => setEditing(false)}
                    onSaved={() => { setEditing(false); fetchKit(); }}
                />
            )}
        </div>
    );
};

const PartSection: React.FC<{
    title: string;
    photos: string[] | null | undefined;
    rows: [string, string | null | undefined][];
}> = ({title, photos, rows}) => (
    <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h2>
        <div className="grid md:grid-cols-2 gap-6">
            <div className="grid grid-cols-2 gap-2">
                {(photos && photos.length > 0) ? photos.map(key => (
                    <div key={key} className="aspect-square rounded-xl overflow-hidden bg-gray-100 dark:bg-ink-700">
                        <AuthImage imageKey={key} alt="" basePath={FOOTBALL_IMAGE_BASE} className="w-full h-full object-cover"/>
                    </div>
                )) : (
                    <div className="aspect-square rounded-xl bg-gray-100 dark:bg-ink-700 flex items-center justify-center text-5xl col-span-2">👕</div>
                )}
            </div>
            <dl className="divide-y divide-gray-100 dark:divide-white/5">
                {rows.map(([label, value]) => (
                    <div key={label} className="flex justify-between py-2 text-sm">
                        <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
                        <dd className="text-gray-900 dark:text-gray-100 font-medium text-right">{value || '—'}</dd>
                    </div>
                ))}
            </dl>
        </div>
    </div>
);

export default FootballItemDetail;
